<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useImageDB } from '../../utils/imageDb'
import { createEditorPreview, createThumb, normalizeImageForUpload } from '../../utils/imageOps'
import { v4 as uuidv4 } from 'uuid'

import { useProjectStore } from '../../store/useProjectStore'
import { useCartStore } from '../../store/cart'
import { uploadImageRecord } from '../../services/uploadService'
import { useToast } from '../../composables/useToast'
import { getWidgetConfig } from '../../runtime/widgetConfig'
import { ACCEPT_IMAGE_INPUT, isAcceptedImageUploadFile, MAX_IMAGE_UPLOAD_MB } from '../../utils/fileAccept'
import TileCropperCanvas from '../editor/TileCropperCanvas.vue'
import { buildMaxCropForRecord, resolveEffectiveAspectRatioForRecord } from '../../utils/cropPreset'

// gql
import { useQuery } from '@vue/apollo-composable'
import {getCategory, getProduct, getProducts} from '../../gql/product/query'
import { mapConfiguratorData } from '../../utils/useConfiguratorData'

// ✅ dein Dropdown
import SizePriceDropdown from '../../components/ui/SizeDropdown.vue'
import type { SizePriceOption } from '../ui/SizeDropdown.vue'

type RecoveryState = {
  orderId?: number
  itemId?: number
  label?: string | null
  originalName?: string | null
  productTitle?: string | null
  categoryTitle?: string | null
  sizeTitle?: string | null
  options?: string[]
  quantity?: number | null
  grayscale?: boolean | null
  cropRequired?: boolean | null
  exp?: number
  sig?: string
  replacementReady?: boolean
  replacementPreviewUrl?: string | null
  replacementThumbUrl?: string | null
  replacementCrop?: Record<string, any> | null
  recoveryStatus?: string | null
  recoveryStatusLabel?: string | null
  recoveryTone?: string | null
  recoveryReasonLabel?: string | null
  recoveryInfoText?: string | null
  customerCanApproveOriginal?: boolean
  primaryReady?: boolean
  completeUrl?: string | null
  approveUrl?: string | null
}

const route = useRoute()
const router = useRouter()
const toast = useToast()

const categoryId = computed(() => Number(route.query.category || route.query.categoryId || 0) || 0)
const productId = computed(() => Number(route.query.product || route.query.productId || 0) || 0)
const sharedProjectCode = computed(() => String(route.query.projectCode || '').trim())
const isPhoneBridgeMode = computed(() => sharedProjectCode.value !== '' || String(route.query.phone || '') === '1')
const isMinimalFlow = computed(() => String(route.query.minimalFlow || '').trim() === '1')
const isSimpleEditorFlow = computed(() => String(route.query.simpleEditorFlow || '').trim() === '1')
const parsedRecoveryState = computed<RecoveryState | null>(() => {
  const raw = String(route.query.recoveryState || '').trim()
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed as RecoveryState : null
  } catch {
    return null
  }
})

const categoryIri = computed(() => (categoryId.value ? `/api/categories/${categoryId.value}` : null))

const { result: categoryData, loading: catLoading, error: catError } = useQuery(
    getCategory,
    () => ({ id: categoryIri.value }),
    () => ({
      enabled: !!categoryIri.value,
      fetchPolicy: 'network-only',
    }),
)

// ✅ mode kommt aus category.editorType (fallback single)
const mode = computed<'single' | 'multi'>(() => {
  const t = categoryData.value?.category?.editorType
  return t === 'multi' ? 'multi' : 'single'
})

const db = useImageDB('imageDB')
const project = useProjectStore()
const cart = useCartStore()
const rootEl = ref<HTMLElement | null>(null)

const selectedSizeKey = ref<string>('')
const selectedProductId = ref<number>(productId.value || 0)
const selectedVariantKeys = ref<Record<string, string>>({})
const files = ref<File[]>([])
const quantity = ref(1)
const grayscale = ref(false)
const busy = ref(false)
const dzOver = ref(false)
const isSimpleFlow = computed(() => String(route.query.simpleFlow || '').trim() === '1')
const minimalPreviewUrl = ref('')
const minimalPrepared = ref<{ blob: Blob; width: number; height: number; mime: string; name: string } | null>(null)
const minimalCropCoords = ref<{ left: number; top: number; width: number; height: number } | null>(null)
const minimalCropPayload = ref<any | null>(null)
const minimalUploadDone = ref(false)
const minimalUploadDoneName = ref('')
const minimalSuccessPreviewUrl = ref('')
const minimalSuccessPreviewAspect = ref(1)
const minimalRecoveryState = ref<RecoveryState | null>(null)
const minimalApproveLoading = ref(false)
const minimalSuccessNotice = ref('')
const simpleEditorDone = ref(false)
const simpleEditorDoneNotice = ref('')

function debugQuickUpload(label: string, payload?: Record<string, any>) {
  if (!isMinimalFlow.value) return
  try {
    console.debug('[quick-upload]', label, {
      minimalUploadDone: minimalUploadDone.value,
      minimalUploadDoneName: minimalUploadDoneName.value,
      minimalSuccessNotice: minimalSuccessNotice.value,
      hasMinimalSuccessPreview: !!minimalSuccessPreviewUrl.value,
      recoveryStatus: minimalRecoveryState.value?.recoveryStatus || null,
      replacementReady: Boolean(minimalRecoveryState.value?.replacementReady),
      ...payload,
    })
  } catch {}
}

function notifyMinimalUploadCompleted(payload: Record<string, any>) {
  if (typeof window === 'undefined') return
  const detail = {
    ...payload,
    source: 'quick-upload',
    minimalFlow: true,
    at: Date.now(),
  }
  try {
    window.dispatchEvent(new CustomEvent('pixelfoto:reupload-uploaded', { detail }))
  } catch {}
  try {
    window.parent?.postMessage({ type: 'pixelfoto:reupload-uploaded', detail }, '*')
  } catch {}
}

function parseBoolQuery(value: unknown, fallback = false) {
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw) return fallback
  if (['1', 'true', 'yes', 'on'].includes(raw)) return true
  if (['0', 'false', 'no', 'off'].includes(raw)) return false
  return fallback
}

function resetMinimalDoneState() {
  minimalUploadDone.value = false
  minimalUploadDoneName.value = ''
  minimalSuccessNotice.value = ''
  simpleEditorDone.value = false
  simpleEditorDoneNotice.value = ''
  resetMinimalSuccessState()
}

const cropRequired = computed(() => parseBoolQuery(route.query.cropRequired, true))
const minimalRecoveryDisplay = computed(() => {
  const state = minimalRecoveryState.value
  const productTitle = String(state?.productTitle || '').trim()
  const sizeTitle = String(state?.sizeTitle || route.query.sizeTitle || '').trim()
  const categoryTitle = String(state?.categoryTitle || '').trim()
  const originalName = String(state?.originalName || '').trim()
  const label = String(state?.label || '').trim()
  const options = Array.isArray(state?.options)
    ? state.options.map((entry) => String(entry || '').trim()).filter(Boolean)
    : []

  return {
    productLine: [productTitle || 'Bildposition', sizeTitle].filter(Boolean).join(' • '),
    categoryTitle,
    originalName,
    label,
    options,
    quantity: Number(state?.quantity || route.query.quantity || quantity.value || 1) || 1,
    grayscale: typeof state?.grayscale === 'boolean' ? state.grayscale : grayscale.value,
    cropRequired: typeof state?.cropRequired === 'boolean' ? state.cropRequired : cropRequired.value,
    statusLabel: String(state?.recoveryStatusLabel || '').trim(),
    statusTone: String(state?.recoveryTone || '').trim(),
    reasonLabel: String(state?.recoveryReasonLabel || '').trim(),
    infoText: String(state?.recoveryInfoText || '').trim(),
  }
})
const minimalStatusClass = computed(() => {
  const tone = minimalRecoveryDisplay.value.statusTone
  if (tone === 'emerald') return 'minimal-pill minimal-pill--success'
  if (tone === 'orange' || tone === 'amber') return 'minimal-pill minimal-pill--warning'
  return 'minimal-pill'
})

function applyMinimalCropPayload(payload: any) {
  if (!payload?.coordinates) return
  minimalCropPayload.value = payload
  minimalCropCoords.value = {
    left: Number(payload.coordinates.left || 0),
    top: Number(payload.coordinates.top || 0),
    width: Math.max(1, Number(payload.coordinates.width || 0)),
    height: Math.max(1, Number(payload.coordinates.height || 0)),
  }
}

function preparedNameForSuccess(file: File | undefined, preparedName?: string | null) {
  const prepared = String(preparedName || '').trim()
  if (prepared) return prepared
  const raw = String(file?.name || '').trim()
  return raw || 'Bild'
}

watch(
  productId,
  (value) => {
    if (!selectedProductId.value && value) {
      selectedProductId.value = value
    }
  },
  { immediate: true },
)

watch(
  () => route.query.quantity,
  (value) => {
    const next = Number(value || 1)
    if (Number.isFinite(next) && next > 0) {
      quantity.value = Math.max(1, Math.round(next))
    }
  },
  { immediate: true },
)

watch(
  () => route.query.grayscale,
  (value) => {
    grayscale.value = parseBoolQuery(value, false)
  },
  { immediate: true },
)

watch(
  parsedRecoveryState,
  async (state) => {
    debugQuickUpload('parsedRecoveryState:incoming', {
      state,
    })
    minimalRecoveryState.value = state
    if (!isMinimalFlow.value) return

    if (state?.replacementReady && (state.replacementPreviewUrl || state.replacementThumbUrl)) {
      minimalUploadDone.value = true
      minimalUploadDoneName.value = 'Nachlieferung'
      minimalSuccessNotice.value = 'Nachlieferung erfolgreich gespeichert.'
      resetMinimalSuccessState()
      minimalSuccessPreviewUrl.value = await buildMinimalRenderedPreviewUrlFromImageUrl(
        String(state.replacementPreviewUrl || state.replacementThumbUrl || ''),
        state.replacementCrop || null,
      )
      debugQuickUpload('parsedRecoveryState:replacementReadyApplied', {
        replacementPreviewUrl: state.replacementPreviewUrl || state.replacementThumbUrl || null,
        replacementCrop: state.replacementCrop || null,
      })
      return
    }

    if (state?.recoveryStatus === 'approved-existing') {
      minimalUploadDone.value = true
      minimalUploadDoneName.value = 'Druckfreigabe gespeichert'
      minimalSuccessNotice.value = 'Druckfreigabe erfolgreich gespeichert.'
      resetMinimalSuccessState()
      debugQuickUpload('parsedRecoveryState:approvedExistingApplied')
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (minimalPreviewUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(minimalPreviewUrl.value)
  }
  if (minimalSuccessPreviewUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(minimalSuccessPreviewUrl.value)
  }
})

const routeSizePayload = computed(() => {
  const widthMm = Number(route.query.sizeWidthMm || 0) || 0
  const heightMm = Number(route.query.sizeHeightMm || 0) || 0
  const title = String(route.query.sizeTitle || '').trim()
  const variantId = String(route.query.sizeVariantId || '').trim()
  if (!widthMm && !heightMm && !title && !variantId) return null
  return {
    title: title || undefined,
    widthMm: widthMm || undefined,
    heightMm: heightMm || undefined,
    variantId: variantId || undefined,
  }
})

const activeSizePayload = computed(() => {
  const selected = selectedSizeKey.value ? sizePayloadFromKey(selectedSizeKey.value) : null
  return selected || routeSizePayload.value
})

function resetMinimalPreviewState() {
  if (minimalPreviewUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(minimalPreviewUrl.value)
  }
  minimalPreviewUrl.value = ''
  minimalPrepared.value = null
  minimalCropCoords.value = null
  minimalCropPayload.value = null
}

function resetMinimalSuccessState() {
  if (minimalSuccessPreviewUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(minimalSuccessPreviewUrl.value)
  }
  minimalSuccessPreviewUrl.value = ''
  minimalSuccessPreviewAspect.value = 1
}

async function buildMinimalRenderedPreviewUrl(input: {
  prepared: { blob: Blob; width: number; height: number } | null
  crop: { left: number; top: number; width: number; height: number } | null
  cropPayload?: any | null
}): Promise<string> {
  const prepared = input.prepared
  if (!prepared) return ''

  const sourceBlobUrl = URL.createObjectURL(prepared.blob)
  let returnedSourceBlobUrl = false
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('Bildvorschau konnte nicht geladen werden.'))
      img.src = sourceBlobUrl
    })

    const rotate = ((Math.round(Number(input.cropPayload?.imageTransforms?.rotate || 0)) % 360) + 360) % 360
    const baseWidth = Math.max(1, Number(input.cropPayload?.imageSize?.width || image.naturalWidth || prepared.width || 1))
    const baseHeight = Math.max(1, Number(input.cropPayload?.imageSize?.height || image.naturalHeight || prepared.height || 1))

    const workingCanvas = document.createElement('canvas')
    const workingCtx = workingCanvas.getContext('2d')
    if (!workingCtx) {
      returnedSourceBlobUrl = true
      return sourceBlobUrl
    }

    if (rotate === 90 || rotate === 270) {
      workingCanvas.width = baseHeight
      workingCanvas.height = baseWidth
    } else {
      workingCanvas.width = baseWidth
      workingCanvas.height = baseHeight
    }

    workingCtx.save()
    if (rotate === 90) {
      workingCtx.translate(workingCanvas.width, 0)
      workingCtx.rotate(Math.PI / 2)
    } else if (rotate === 180) {
      workingCtx.translate(workingCanvas.width, workingCanvas.height)
      workingCtx.rotate(Math.PI)
    } else if (rotate === 270) {
      workingCtx.translate(0, workingCanvas.height)
      workingCtx.rotate(-Math.PI / 2)
    }
    workingCtx.drawImage(image, 0, 0, baseWidth, baseHeight)
    workingCtx.restore()

    const crop = input.crop
    if (!crop) {
      const blob = await new Promise<Blob | null>((resolve) => workingCanvas.toBlob(resolve, 'image/jpeg', 0.96))
      if (!blob) {
        returnedSourceBlobUrl = true
        return sourceBlobUrl
      }
      return URL.createObjectURL(blob)
    }

    const cropX = Math.max(0, Math.min(workingCanvas.width - 1, Math.round(Number(crop.left || 0))))
    const cropY = Math.max(0, Math.min(workingCanvas.height - 1, Math.round(Number(crop.top || 0))))
    const cropWidth = Math.max(1, Math.min(workingCanvas.width - cropX, Math.round(Number(crop.width || 1))))
    const cropHeight = Math.max(1, Math.min(workingCanvas.height - cropY, Math.round(Number(crop.height || 1))))

    const resultCanvas = document.createElement('canvas')
    resultCanvas.width = cropWidth
    resultCanvas.height = cropHeight
    const resultCtx = resultCanvas.getContext('2d')
    if (!resultCtx) {
      returnedSourceBlobUrl = true
      return sourceBlobUrl
    }
    resultCtx.drawImage(
      workingCanvas,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    )

    minimalSuccessPreviewAspect.value = cropWidth / Math.max(1, cropHeight)

    const blob = await new Promise<Blob | null>((resolve) => resultCanvas.toBlob(resolve, 'image/jpeg', 0.96))
    if (!blob) {
      returnedSourceBlobUrl = true
      return sourceBlobUrl
    }
    return URL.createObjectURL(blob)
  } finally {
    if (!returnedSourceBlobUrl) URL.revokeObjectURL(sourceBlobUrl)
  }
}

async function buildMinimalRenderedPreviewUrlFromImageUrl(
  imageUrl: string,
  crop: Record<string, any> | null,
): Promise<string> {
  const source = String(imageUrl || '').trim()
  if (!source) return ''

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Bildvorschau konnte nicht geladen werden.'))
    img.src = source
  })

  const sourceWidth = Math.max(1, Number(image.naturalWidth || 1))
  const sourceHeight = Math.max(1, Number(image.naturalHeight || 1))
  const nx = Math.max(0, Math.min(1, Number(crop?.nx ?? 0)))
  const ny = Math.max(0, Math.min(1, Number(crop?.ny ?? 0)))
  const nw = Math.max(0.0001, Math.min(1, Number(crop?.nw ?? 1)))
  const nh = Math.max(0.0001, Math.min(1, Number(crop?.nh ?? 1)))
  const cropX = Math.max(0, Math.min(sourceWidth - 1, Math.round(nx * sourceWidth)))
  const cropY = Math.max(0, Math.min(sourceHeight - 1, Math.round(ny * sourceHeight)))
  const cropWidth = Math.max(1, Math.min(sourceWidth - cropX, Math.round(nw * sourceWidth)))
  const cropHeight = Math.max(1, Math.min(sourceHeight - cropY, Math.round(nh * sourceHeight)))

  const canvas = document.createElement('canvas')
  canvas.width = cropWidth
  canvas.height = cropHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return source
  ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)
  minimalSuccessPreviewAspect.value = cropWidth / Math.max(1, cropHeight)

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.96))
  return blob ? URL.createObjectURL(blob) : source
}

function buildMinimalInitialCrop(width: number, height: number) {
  const size = activeSizePayload.value
  const crop = buildMaxCropForRecord({
    width,
    height,
    editorWidth: width,
    editorHeight: height,
    edits: {
      lockAR: true,
      orientMode: 'auto',
      swapAR: false,
    },
  } as any, size as any)

  if (!crop) return null

  return {
    left: Math.round(Number(crop.x || 0)),
    top: Math.round(Number(crop.y || 0)),
    width: Math.max(1, Math.round(Number(crop.w || width))),
    height: Math.max(1, Math.round(Number(crop.h || height))),
  }
}

const cropAspectRatio = computed(() => {
  const size = activeSizePayload.value
  if (!size || !minimalPrepared.value) return 0
  const ar = resolveEffectiveAspectRatioForRecord({
    width: minimalPrepared.value.width,
    height: minimalPrepared.value.height,
    editorWidth: minimalPrepared.value.width,
    editorHeight: minimalPrepared.value.height,
    edits: {
      rotate: 0,
      lockAR: true,
      orientMode: 'auto',
      swapAR: false,
    },
  } as any, size as any)
  if (ar && Number.isFinite(ar) && ar > 0) return ar
  return 0
})

async function setFiles(next: File[]) {
  resetMinimalDoneState()
  const imgs = next.filter((f) => isAcceptedImageUploadFile(f))
  files.value = (isMinimalFlow.value || isSimpleEditorFlow.value) ? imgs.slice(0, 1) : imgs
  resetMinimalPreviewState()
  const rejected = next.length - imgs.length
  if (rejected > 0) {
    toast.error(`Einige Dateien wurden übersprungen. Erlaubt sind nur Bilder bis ${MAX_IMAGE_UPLOAD_MB} MB.`)
  }
  if ((isMinimalFlow.value || isSimpleEditorFlow.value) && imgs.length > 1) {
    toast.error('Für diesen Nachlieferungs-Upload ist nur ein Bild pro Kachel vorgesehen.')
  }

  if ((isMinimalFlow.value || isSimpleEditorFlow.value) && files.value[0]) {
    try {
      const prepared = await normalizeImageForUpload(files.value[0], files.value[0].name || 'bild.jpg', { quality: 0.92 })
      minimalPrepared.value = {
        blob: prepared.blob,
        width: prepared.width,
        height: prepared.height,
        mime: prepared.mime,
        name: prepared.name,
      }
      minimalPreviewUrl.value = URL.createObjectURL(prepared.blob)
      minimalCropPayload.value = null
      minimalCropCoords.value = buildMinimalInitialCrop(prepared.width, prepared.height)
    } catch (error: any) {
      files.value = []
      resetMinimalPreviewState()
      toast.error(String(error?.message || 'Bild konnte nicht vorbereitet werden.'))
    }
  }
}

function onPick(e: Event) {
  const input = e.target as HTMLInputElement
  const next = input.files ? Array.from(input.files) : []
  input.value = ''
  void setFiles(next)
}

function onDrop(e: DragEvent) {
  dzOver.value = false
  const next = Array.from(e.dataTransfer?.files || [])
  void setFiles(next)
}

const productIri = computed(() => (productId.value ? `/api/products/${productId.value}` : null))
const effectiveProductId = computed(() => Number(selectedProductId.value || productId.value || 0) || 0)
const effectiveProductIri = computed(() => (effectiveProductId.value ? `/api/products/${effectiveProductId.value}` : null))

const { result: productData, loading: prodLoading, error: prodError } = useQuery(
    getProduct,
    () => ({ id: effectiveProductIri.value }),
    () => ({
      enabled: !!effectiveProductIri.value,
      fetchPolicy: 'network-only',
    }),
)

const { result: productsData, loading: productsLoading } = useQuery(
  getProducts,
  () => ({
    pageSize: 100,
    category: categoryIri.value,
    isPublished: true,
    isDeleted: false,
  }),
  () => ({
    enabled: isSimpleEditorFlow.value && !!categoryIri.value,
    fetchPolicy: 'network-only',
  }),
)

const mappedCfg = computed(() => {
  const p = (productData.value?.product ?? null) as any
  return p ? mapConfiguratorData(p) : null
})

const productListOptions = computed(() => {
  const edges = productsData.value?.products?.edges || []
  return edges
    .map((edge: any) => edge?.node)
    .filter(Boolean)
    .map((node: any) => ({
      key: String(node.id || '').split('/').pop() || String(node._id || ''),
      title: String(node.title || '').trim() || 'Produkt',
      price: Number(node.price || 0) / 100,
      priceLabel: typeof node.price === 'number'
        ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(node.price || 0) / 100)
        : '—',
    }))
})

const sizeGroup = computed(() => mappedCfg.value?.groups?.find((g: any) => g.isSize) || null)
const sizeOptionsRaw = computed(() => sizeGroup.value?.sizeOptions ?? [])
const variantGroups = computed(() => (mappedCfg.value?.groups || []).filter((group: any) => !group.isSize))

const dropdownOptions = computed<SizePriceOption[]>(() =>
    sizeOptionsRaw.value.map((o: any, idx: number) => {
      const key = String(o.variantId || o.id || `${idx}-${o.title}`)
      const price = o.priceSpecial ?? o.price ?? 0
      const priceLabel =
          price != null && Number(price) > 0
              ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(price))
              : '—'

      return { key, title: String(o.title || ''), price: Number(price) || 0, priceLabel }
    }),
)

function optionDropdownOptions(group: any): SizePriceOption[] {
  return (group?.options || []).map((entry: any) => {
    const price = Number(entry?.price || 0)
    return {
      key: String(entry?.id || ''),
      title: String(entry?.title || ''),
      price,
      priceLabel: price > 0
        ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price)
        : '—',
    }
  })
}

const prefilledSizeVariantId = computed(() => String(route.query.sizeVariantId || '').trim())
const prefilledSizeTitle = computed(() => String(route.query.sizeTitle || '').trim())

watch(
  [dropdownOptions, prefilledSizeVariantId, prefilledSizeTitle],
  ([options, variantId, title]) => {
    if (selectedSizeKey.value) return
    const hit = options.find((option) => {
      if (variantId && String(option.key) === variantId) return true
      if (variantId && String((sizeOptionsRaw.value.find((o: any, idx: number) => String(o.variantId || o.id || `${idx}-${o.title}`) === option.key)?.variantId || '')) === variantId) return true
      if (title && String(option.title || '').trim() === title) return true
      return false
    })
    if (hit) {
      selectedSizeKey.value = hit.key
    }
  },
  { immediate: true },
)

watch(
  productListOptions,
  (options) => {
    if (!isSimpleEditorFlow.value) return
    if (selectedProductId.value && options.some((option) => Number(option.key) === Number(selectedProductId.value))) return
    const preferred = options.find((option) => Number(option.key) === Number(productId.value || 0)) || options[0]
    if (preferred) {
      selectedProductId.value = Number(preferred.key) || 0
    }
  },
  { immediate: true },
)

watch(
  variantGroups,
  (groups) => {
    const next: Record<string, string> = {}
    for (const group of groups) {
      const options = Array.isArray(group?.options) ? group.options : []
      const current = selectedVariantKeys.value[group.id]
      const hit = options.find((entry: any) => String(entry.id) === String(current))
      const fallback = hit || options.find((entry: any) => entry.isDefault) || options[0]
      if (fallback) next[group.id] = String(fallback.id)
    }
    selectedVariantKeys.value = next
  },
  { immediate: true },
)

function sizePayloadFromKey(key: string) {
  const opt = sizeOptionsRaw.value.find((o: any, idx: number) => String(o.variantId || o.id || `${idx}-${o.title}`) === key)
  if (!opt) return null
  return {
    title: opt.title,
    widthMm: opt.widthMm,
    heightMm: opt.heightMm,
    variantId: opt.variantId || undefined,
  }
}

function buildSimpleEditorPreset(sizePayload: any) {
  const options = variantGroups.value.map((group: any) => {
    const optionId = String(selectedVariantKeys.value[group.id] || '').trim()
    const hit = (group.options || []).find((entry: any) => String(entry.id) === optionId)
    if (!hit) return null
    return {
      groupId: group.id,
      variantId: hit.id,
      value: hit.title,
    }
  }).filter(Boolean)

  return {
    productId: effectiveProductId.value,
    productTitle: String(mappedCfg.value?.product?.title || '').trim() || undefined,
    size: sizePayload ? {
      variantId: sizePayload.variantId,
      title: sizePayload.title,
      widthMm: sizePayload.widthMm,
      heightMm: sizePayload.heightMm,
    } : undefined,
    options,
    quantity: Math.max(1, Number(quantity.value || 1)),
    grayscale: Boolean(grayscale.value),
    categoryId: categoryId.value,
    categoryTitle: String(categoryData.value?.category?.title || '').trim() || undefined,
    lockAR: true,
    orientMode: 'auto' as const,
    swapAR: false,
  }
}

const canUpload = computed(() => {
  if (!categoryId.value || !(isSimpleEditorFlow.value ? effectiveProductId.value : productId.value)) return false
  if (busy.value) return false
  if (!isMinimalFlow.value && catLoading.value) return false
  if (!isMinimalFlow.value && catError.value) return false
  if ((isMinimalFlow.value || isSimpleEditorFlow.value) && !activeSizePayload.value) return false
  return files.value.length > 0 && (isPhoneBridgeMode.value || !!selectedSizeKey.value)
})

const simpleEditorMeta = computed(() => {
  const productTitle = String(mappedCfg.value?.product?.title || '').trim()
  const sizeTitle = String(activeSizePayload.value?.title || '').trim()
  const optionTitles = variantGroups.value
    .map((group: any) => {
      const key = selectedVariantKeys.value[group.id]
      const hit = (group.options || []).find((entry: any) => String(entry.id) === String(key))
      return String(hit?.title || '').trim()
    })
    .filter(Boolean)
  return {
    productTitle,
    sizeTitle,
    optionTitles,
  }
})

async function uploadAndGo() {
  debugQuickUpload('uploadAndGo:start', {
    categoryId: categoryId.value,
    productId: isSimpleEditorFlow.value ? effectiveProductId.value : productId.value,
    files: files.value.map(file => ({ name: file.name, size: file.size, type: file.type })),
    activeSizePayload: activeSizePayload.value,
    cropRequired: cropRequired.value,
    canUpload: canUpload.value,
  })
  if (!categoryId.value || !(isSimpleEditorFlow.value ? effectiveProductId.value : productId.value)) return toast.error('categoryId/productId fehlt')
  if (!files.value.length) return toast.error('Bitte Dateien auswählen')
  if (!activeSizePayload.value) return toast.error('Bitte Größe wählen')
  if (isMinimalFlow.value && cropRequired.value && !minimalCropCoords.value) return toast.error('Ausschnitt ist noch nicht bereit.')

  busy.value = true
  try {
    const code = await project.ensureProject(sharedProjectCode.value || null)
    if (!code) throw new Error('projectCode missing')

    const sizePayload = activeSizePayload.value
    if (!isPhoneBridgeMode.value && !sizePayload) throw new Error('Size payload missing')
    const targetProductId = isSimpleEditorFlow.value ? effectiveProductId.value : productId.value
    const simplePreset = isSimpleEditorFlow.value ? buildSimpleEditorPreset(sizePayload) : null

    const imageIds: string[] = []

    // 1) in DB speichern (kein Preview UI nötig, aber Editor/Upload braucht die Records)
    for (const f of files.value) {
      const id = uuidv4()
      imageIds.push(id)

      const prepared = isMinimalFlow.value && minimalPrepared.value
        ? minimalPrepared.value
        : await normalizeImageForUpload(f, f.name || id, { quality: 0.92 })
      const originalBlob = prepared.blob
      const workingBlob = prepared.blob
      const dims = { width: prepared.width, height: prepared.height }
      const cropPayload = isMinimalFlow.value ? minimalCropPayload.value : null
      const cropImageSize = cropPayload?.imageSize || null
      const cropRotate = Math.round(Number(cropPayload?.imageTransforms?.rotate || 0))
      const minimalCrop = isMinimalFlow.value && minimalCropCoords.value
        ? {
            x: Math.round(minimalCropCoords.value.left),
            y: Math.round(minimalCropCoords.value.top),
            w: Math.max(1, Math.round(minimalCropCoords.value.width)),
            h: Math.max(1, Math.round(minimalCropCoords.value.height)),
            rot: cropRotate,
            sourceWidth: Math.max(1, Math.round(Number(cropImageSize?.width || prepared.width || 1))),
            sourceHeight: Math.max(1, Math.round(Number(cropImageSize?.height || prepared.height || 1))),
            nx: Math.max(0, Math.min(1, Number(minimalCropCoords.value.left) / Math.max(1, Math.round(Number(cropImageSize?.width || prepared.width || 1))))),
            ny: Math.max(0, Math.min(1, Number(minimalCropCoords.value.top) / Math.max(1, Math.round(Number(cropImageSize?.height || prepared.height || 1))))),
            nw: Math.max(0, Math.min(1, Number(minimalCropCoords.value.width) / Math.max(1, Math.round(Number(cropImageSize?.width || prepared.width || 1))))),
            nh: Math.max(0, Math.min(1, Number(minimalCropCoords.value.height) / Math.max(1, Math.round(Number(cropImageSize?.height || prepared.height || 1))))),
          }
        : undefined

      // thumb optional (macht später UI schneller)
      const thumb = await createThumb(workingBlob, 280).catch(() => null)
      const editorPreview = isMinimalFlow.value
        ? {
            blob: workingBlob,
            width: dims.width,
            height: dims.height,
          }
        : await createEditorPreview(workingBlob, 1600, 'image/webp', 0.82).catch(() => null)

      await db.put({
        id,
        name: prepared.name,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        mime: prepared.mime,
        bytes: workingBlob.size,
        width: dims.width,
        height: dims.height,
        originalBlob,
        workingBlob,
        editorBlob: editorPreview?.blob,
        editorWidth: editorPreview?.width,
        editorHeight: editorPreview?.height,
        thumbBlob: thumb?.blob,
        thumbWidth: thumb?.width,
        thumbHeight: thumb?.height,
        categoryId: categoryId.value,
        productId: targetProductId,
        categoryTitle: String(categoryData.value?.category?.title || '').trim() || undefined,
        productTitle: isSimpleEditorFlow.value ? String(mappedCfg.value?.product?.title || '').trim() || undefined : undefined,
        edits: {
          ...(minimalCrop ? { crop: minimalCrop } : {}),
          ...(isMinimalFlow.value ? { lockAR: true, swapAR: false, orientMode: 'auto' as const } : {}),
          preset: isSimpleEditorFlow.value
            ? {
                ...(simplePreset || {}),
                ...(minimalCrop ? { crop: minimalCrop } : {}),
              }
            : {
                productId: targetProductId,
                ...(sizePayload ? { size: sizePayload } : {}),
                options: [],
                quantity: Math.max(1, Number(quantity.value || 1)),
                grayscale: !!grayscale.value,
                ...(isMinimalFlow.value ? { lockAR: true, swapAR: false, orientMode: 'auto' as const } : {}),
                ...(minimalCrop ? { crop: minimalCrop } : {}),
              },
        },
        upload: { state: 'queued', tries: 0, updatedAt: Date.now() },
      } as any)
    }

    // 2) Upload direkt (ohne Runner UI)
    for (const id of imageIds) {
      const rec = await db.get(id)
      if (rec) await uploadImageRecord(rec as any, code)
    }

    debugQuickUpload('uploadAndGo:uploadsFinished', {
      projectCode: code,
      imageIds,
    })
    toast.success(`${imageIds.length} Bild(er) hochgeladen`)

    if (isSimpleEditorFlow.value) {
      const imageId = imageIds[0]
      const record = imageId ? await db.get(imageId) : null
      if (!record) {
        throw new Error('Das hochgeladene Bild konnte nicht geladen werden.')
      }
      await cart.syncImageRecord(record as any, code)
      const cfg = getWidgetConfig() as any
      const cartUrl = String(cfg.cartUrl || '').trim()
      if (cartUrl) {
        window.location.assign(cartUrl)
        return
      }
      simpleEditorDone.value = true
      simpleEditorDoneNotice.value = 'Bild wurde direkt in den Warenkorb gelegt.'
      files.value = []
      resetMinimalPreviewState()
      toast.success(simpleEditorDoneNotice.value)
      return
    }

    if (isPhoneBridgeMode.value && isSimpleFlow.value && !isMinimalFlow.value && cropRequired.value) {
      await router.push({
        path: '/multi',
        query: {
          product: String(productId.value),
          category: String(categoryId.value),
          imageIds: imageIds.join(','),
          projectCode: code,
          embedded: 'tile',
        },
      })
      files.value = []
      resetMinimalPreviewState()
      selectedSizeKey.value = ''
      return
    }

    if (isPhoneBridgeMode.value && isMinimalFlow.value) {
      const localPreviewUrl = await buildMinimalRenderedPreviewUrl({
        prepared: minimalPrepared.value,
        crop: minimalCropCoords.value,
        cropPayload: minimalCropPayload.value,
      })

      let completedItem: Record<string, any> | null = null
      const completeUrl = String(minimalRecoveryState.value?.completeUrl || '').trim()
      if (completeUrl) {
        try {
          debugQuickUpload('uploadAndGo:completeCall:start', {
            completeUrl,
          })
          const completeResponse = await fetch(completeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          })
          const completePayload = await completeResponse.json().catch(() => null)
          debugQuickUpload('uploadAndGo:completeCall:response', {
            ok: completeResponse.ok,
            status: completeResponse.status,
            completePayload,
          })
          if (!completeResponse.ok || !completePayload?.success) {
            throw new Error(String(completePayload?.error?.message || 'Nachlieferung konnte nicht abgeschlossen werden.'))
          }
          completedItem = completePayload.item || null
        } catch (error: any) {
          debugQuickUpload('uploadAndGo:completeCall:error', {
            message: String(error?.message || error),
          })
          toast.error(String(error?.message || 'Nachlieferung konnte nicht abgeschlossen werden.'))
        }
      }

      minimalUploadDone.value = true
      minimalUploadDoneName.value = String(preparedNameForSuccess(files.value[0], minimalPrepared.value?.name) || 'Bild')
      minimalSuccessPreviewUrl.value = localPreviewUrl
      minimalSuccessNotice.value = cropRequired.value
        ? 'Bild erfolgreich gespeichert und Ausschnitt übernommen.'
        : 'Bild erfolgreich gespeichert.'
      minimalRecoveryState.value = {
        ...(minimalRecoveryState.value || {}),
        replacementReady: true,
        recoveryStatus: 'replacement-success',
        recoveryStatusLabel: 'Nachlieferung erfolgreich',
        recoveryTone: 'emerald',
        customerCanApproveOriginal: false,
      }
      debugQuickUpload('uploadAndGo:localSuccessApplied', {
        localPreviewUrl,
      })
      if (completedItem?.replacementReady && (completedItem?.replacementPreviewUrl || completedItem?.replacementThumbUrl)) {
        resetMinimalSuccessState()
        minimalSuccessPreviewUrl.value = await buildMinimalRenderedPreviewUrlFromImageUrl(
          String(completedItem.replacementPreviewUrl || completedItem.replacementThumbUrl || ''),
          completedItem.replacementCrop || null,
        )
        minimalRecoveryState.value = {
          ...(minimalRecoveryState.value || {}),
          ...completedItem,
          replacementReady: true,
          replacementPreviewUrl: completedItem.replacementPreviewUrl || null,
          replacementThumbUrl: completedItem.replacementThumbUrl || null,
          replacementCrop: completedItem.replacementCrop || null,
          recoveryStatus: completedItem.recoveryStatus || 'replacement-success',
          recoveryStatusLabel: completedItem.recoveryStatusLabel || 'Nachlieferung erfolgreich',
          recoveryTone: completedItem.recoveryTone || 'emerald',
          recoveryReasonLabel: completedItem.recoveryReasonLabel || null,
          recoveryInfoText: completedItem.recoveryInfoText || null,
          customerCanApproveOriginal: Boolean(completedItem.customerCanApproveOriginal),
          primaryReady: Boolean(completedItem.primaryReady),
        }
        debugQuickUpload('uploadAndGo:completedItemMerged', {
          completedItem,
        })
      }
      notifyMinimalUploadCompleted({
        projectCode: code,
        productId: productId.value,
        categoryId: categoryId.value,
        imageIds,
      })
      debugQuickUpload('uploadAndGo:beforeReset')
      files.value = []
      resetMinimalPreviewState()
      toast.success('Bild erfolgreich gespeichert')
      debugQuickUpload('uploadAndGo:done')
      return
    }

    if (isPhoneBridgeMode.value) {
      files.value = []
      resetMinimalPreviewState()
      selectedSizeKey.value = ''
      return
    }

    // 3) Redirect in Haupteditor
    const cfg = getWidgetConfig() as any
    const targetUrl = cfg.quickUploadTargetUrl || (route.query.target as string | undefined)

    if (targetUrl) {
      const u = new URL(targetUrl)
      u.searchParams.set('product', String(productId.value))
      u.searchParams.set('category', String(categoryId.value))
      u.searchParams.set('imageIds', imageIds.join(','))
      u.searchParams.set('mode', mode.value)
      window.location.assign(u.toString())
      return
    }

    // fallback: internal
    await router.push({
      path: mode.value === 'multi' ? '/multi' : '/single',
      query: {
        product: String(productId.value),
        category: String(categoryId.value),
        imageIds: imageIds.join(','),
      },
    })
  } catch (e: any) {
    debugQuickUpload('uploadAndGo:error', {
      message: String(e?.message || e),
      stack: String(e?.stack || ''),
    })
    toast.error(e?.message ?? 'Upload fehlgeschlagen')
  } finally {
    busy.value = false
    debugQuickUpload('uploadAndGo:finally')
  }
}

async function approveExistingRecoveryImage() {
  const approveUrl = String(minimalRecoveryState.value?.approveUrl || '').trim()
  if (!approveUrl || minimalApproveLoading.value) return

  minimalApproveLoading.value = true
  try {
    debugQuickUpload('approveExistingRecoveryImage:start', {
      approveUrl,
    })
    const response = await fetch(approveUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    const payload = await response.json().catch(() => null)
    debugQuickUpload('approveExistingRecoveryImage:response', {
      ok: response.ok,
      status: response.status,
      payload,
    })
    if (!response.ok || !payload?.success) {
      throw new Error(String(payload?.error?.message || 'Druckfreigabe konnte nicht gespeichert werden.'))
    }

    minimalRecoveryState.value = {
      ...(minimalRecoveryState.value || {}),
      ...(payload.item || {}),
      recoveryStatus: payload.item?.recoveryStatus || 'approved-existing',
      recoveryStatusLabel: payload.item?.recoveryStatusLabel || 'Druckfreigabe erteilt',
      customerCanApproveOriginal: false,
    }
    minimalUploadDone.value = true
    minimalUploadDoneName.value = 'Druckfreigabe gespeichert'
    minimalSuccessNotice.value = 'Druckfreigabe erfolgreich gespeichert.'
    resetMinimalSuccessState()
    debugQuickUpload('approveExistingRecoveryImage:done')
    toast.success('Druckfreigabe gespeichert')
  } catch (error: any) {
    debugQuickUpload('approveExistingRecoveryImage:error', {
      message: String(error?.message || error),
    })
    toast.error(String(error?.message || 'Druckfreigabe konnte nicht gespeichert werden.'))
  } finally {
    minimalApproveLoading.value = false
    debugQuickUpload('approveExistingRecoveryImage:finally')
  }
}
</script>

<template>
  <section ref="rootEl" class="qu" :class="{ 'qu--minimal': isMinimalFlow }">
    <div class="box" :class="{ 'box--minimal': isMinimalFlow }">
      <header v-if="!isMinimalFlow" class="head">
        <div>
          <div class="kicker">{{ isSimpleEditorFlow ? 'Simple Editor' : (isSimpleFlow ? 'Bildeditor' : 'Quick Upload') }}</div>
          <div class="title">{{ isSimpleEditorFlow ? 'Bild direkt konfigurieren' : (isMinimalFlow ? 'Bild hochladen' : (isSimpleFlow ? 'Bild hochladen und Ausschnitt festlegen' : 'Bilder hochladen')) }}</div>
          <div class="sub">{{ isSimpleEditorFlow ? 'Ein Bild hochladen, Produkt und Varianten wählen und direkt in den Warenkorb legen.' : (isMinimalFlow ? 'Ein Bild hochladen und nur bei Bedarf den Ausschnitt setzen.' : (isSimpleFlow ? 'Schritt für Schritt zum fertigen Bild.' : 'Dateien wählen + Größe — dann direkt in den Editor.')) }}</div>
        </div>

        <div v-if="!isSimpleFlow && !isMinimalFlow" class="meta">
          <span class="pill">Kategorie <b>#{{ categoryId || '—' }}</b></span>
          <span class="pill">Produkt <b>#{{ productId || '—' }}</b></span>
          <span class="pill">Modus <b>{{ mode }}</b></span>
        </div>
      </header>

      <div v-if="prodLoading && !isMinimalFlow" class="state">Lade Produkt…</div>
      <div v-else-if="prodError && !isMinimalFlow" class="err">Fehler: {{ prodError.message }}</div>

      <div v-else class="content">
        <div v-if="isMinimalFlow" class="minimal-meta-card">
          <div class="minimal-meta-top">
            <div class="minimal-meta-copy">
              <div class="minimal-product-line">{{ minimalRecoveryDisplay.productLine }}</div>
              <div v-if="minimalRecoveryDisplay.label" class="minimal-label">{{ minimalRecoveryDisplay.label }}</div>
            </div>
            <span v-if="minimalRecoveryDisplay.statusLabel" :class="minimalStatusClass">
              {{ minimalRecoveryDisplay.statusLabel }}
            </span>
          </div>

          <div class="minimal-meta-grid">
            <div v-if="minimalRecoveryDisplay.originalName"><strong>Original:</strong> {{ minimalRecoveryDisplay.originalName }}</div>
            <div v-if="minimalRecoveryDisplay.categoryTitle"><strong>Kategorie:</strong> {{ minimalRecoveryDisplay.categoryTitle }}</div>
            <div><strong>Ausschnitt:</strong> {{ minimalRecoveryDisplay.cropRequired ? 'Direkt im Widget setzen' : 'Nicht erforderlich' }}</div>
            <div v-if="minimalRecoveryDisplay.quantity"><strong>Menge:</strong> {{ minimalRecoveryDisplay.quantity }}</div>
            <div v-if="minimalRecoveryDisplay.grayscale"><strong>Ausgabe:</strong> Schwarzweiß</div>
          </div>

          <div v-if="minimalRecoveryDisplay.options.length" class="minimal-option-list">
            <span v-for="entry in minimalRecoveryDisplay.options" :key="entry" class="minimal-pill">{{ entry }}</span>
          </div>

          <div v-if="minimalRecoveryDisplay.reasonLabel || minimalRecoveryDisplay.infoText" class="minimal-note">
            <div v-if="minimalRecoveryDisplay.reasonLabel"><strong>Hinweis:</strong> {{ minimalRecoveryDisplay.reasonLabel }}</div>
            <div v-if="minimalRecoveryDisplay.infoText"><strong>Info:</strong> {{ minimalRecoveryDisplay.infoText }}</div>
          </div>
        </div>

        <div v-if="(isSimpleFlow || isSimpleEditorFlow) && !isMinimalFlow" class="steps">
          <template v-if="isSimpleEditorFlow">
            <span class="step-pill">1. Produkt</span>
            <span class="step-pill">2. Varianten</span>
            <span class="step-pill">3. Bild</span>
            <span class="step-pill">4. In den Warenkorb</span>
          </template>
          <template v-else>
          <span class="step-pill">1. Größe</span>
          <span class="step-pill">2. Bild</span>
          <span class="step-pill">3. Anzahl &amp; S/W</span>
          <span class="step-pill">4. Ausschnitt</span>
          </template>
        </div>

        <div v-if="isSimpleEditorFlow" class="simple-editor-panel">
          <div class="simple-editor-panel__head">
            <div class="simple-editor-panel__kicker">Konfiguration</div>
            <div class="simple-editor-panel__title">Produkt und Varianten</div>
          </div>

        <div class="row row--simple">
          <div class="row-head">
            <div class="row-title">1. Produkt auswählen</div>
            <div class="row-sub muted">{{ productsLoading ? 'Lade Produkte…' : 'Pflichtfeld' }}</div>
          </div>

          <div class="row-body row-body--stack">
            <SizePriceDropdown
              class="sd"
              :options="productListOptions"
              :model-value="String(selectedProductId || '')"
              :disabled="!productListOptions.length || busy"
              placeholder="— Produkt wählen —"
              @update:model-value="(key: string) => { selectedProductId = Number(key) || 0; selectedSizeKey = '' }"
            />
          </div>
        </div>

        <!-- Größe -->
        <div v-if="!isMinimalFlow" class="row" :class="{ 'row--simple': isSimpleEditorFlow }">
          <div class="row-head">
            <div class="row-title">{{ isSimpleEditorFlow ? '2. Größe auswählen' : (isSimpleFlow ? '1. Größe auswählen' : 'Größe') }}</div>
            <div class="row-sub muted">{{ isPhoneBridgeMode ? 'Optional' : 'Pflichtfeld' }}</div>
          </div>

          <div class="row-body row-body--stack">
            <SizePriceDropdown
                class="sd"
                :options="dropdownOptions"
                :model-value="selectedSizeKey"
                :disabled="!dropdownOptions.length || busy"
                placeholder="— Größe wählen —"
                @update:model-value="(k: string) => (selectedSizeKey = k)"
            />
            <div v-if="!dropdownOptions.length" class="hint muted">Keine Größenoptionen gefunden.</div>
          </div>
        </div>

        <div v-if="isSimpleEditorFlow && variantGroups.length" class="row row--simple">
          <div class="row-head">
            <div class="row-title">3. Varianten auswählen</div>
            <div class="row-sub muted">Wie im Postupload-Overlay</div>
          </div>

          <div class="row-body row-body--stack">
            <div v-for="group in variantGroups" :key="group.id" class="variant-line">
              <div class="variant-label">{{ group.title }}</div>
              <SizePriceDropdown
                class="sd"
                :options="optionDropdownOptions(group)"
                :model-value="selectedVariantKeys[group.id] || ''"
                :disabled="!(group.options || []).length || busy"
                :placeholder="`— ${group.title} wählen —`"
                @update:model-value="(key: string) => { selectedVariantKeys[group.id] = key }"
              />
            </div>
          </div>
        </div>
        </div>

        <!-- Dateien -->
        <div
            class="row dz"
            :class="{ over: dzOver, 'dz--minimal': isMinimalFlow }"
            @dragenter.prevent="dzOver = true"
            @dragover.prevent="dzOver = true"
            @dragleave.prevent="dzOver = false"
            @drop.prevent="onDrop"
        >
          <div class="row-head">
            <div class="row-title">{{ isMinimalFlow ? 'Bild hochladen' : (isSimpleEditorFlow ? '4. Bild hochladen' : (isSimpleFlow ? '2. Bild hochladen' : 'Dateien')) }}</div>
            <div class="row-sub muted">{{ files.length ? files.length + ' Datei(en)' : 'Keine' }}</div>
          </div>

          <div class="row-body" :class="{ 'row-body--minimal-upload': isMinimalFlow }">
            <div v-if="isMinimalFlow" class="minimal-stage">
              <div v-if="minimalUploadDone" class="minimal-success minimal-success--standalone">
                <div v-if="minimalSuccessPreviewUrl" class="minimal-success-preview" :style="{ aspectRatio: String(Math.max(0.1, minimalSuccessPreviewAspect || 1)) }">
                  <div class="minimal-success-preview-frame">
                    <img
                      :src="minimalSuccessPreviewUrl"
                      alt="Gespeicherter Ausschnitt"
                      class="minimal-success-preview-image"
                      :class="{ 'minimal-success-preview-image--grayscale': minimalRecoveryDisplay.grayscale }"
                    >
                  </div>
                </div>
                <div class="minimal-success-meta">
                  <div class="minimal-success-banner">
                    <span class="minimal-success-banner-dot" />
                    {{ minimalSuccessNotice || 'Bild erfolgreich gespeichert.' }}
                  </div>
                  <div class="minimal-success-icon">✓</div>
                  <div class="minimal-success-title">{{ minimalRecoveryState?.recoveryStatusLabel || 'Bild gespeichert' }}</div>
                  <div class="minimal-success-copy">
                    <template v-if="minimalRecoveryState?.recoveryStatus === 'approved-existing'">
                      Die Druckfreigabe für das vorhandene Bild wurde gespeichert.
                    </template>
                    <template v-else>
                      {{ minimalUploadDoneName || 'Das Bild' }} wurde hochgeladen
                      <span v-if="cropRequired">und mit Ausschnitt gespeichert.</span>
                      <span v-else>und direkt übernommen.</span>
                    </template>
                  </div>
                  <div v-if="minimalRecoveryDisplay.reasonLabel || minimalRecoveryDisplay.infoText" class="minimal-success-note">
                    <div v-if="minimalRecoveryDisplay.reasonLabel"><strong>Hinweis:</strong> {{ minimalRecoveryDisplay.reasonLabel }}</div>
                    <div v-if="minimalRecoveryDisplay.infoText"><strong>Info:</strong> {{ minimalRecoveryDisplay.infoText }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="minimal-canvas" :class="{ 'minimal-canvas--filled': !!minimalPreviewUrl }">
                <TileCropperCanvas
                  v-if="minimalPreviewUrl && cropRequired"
                  class="minimal-cropper"
                  :src="minimalPreviewUrl"
                  :coordinates="minimalCropCoords"
                  :stencil-props="{ aspectRatio: cropAspectRatio || undefined }"
                  :image-width="minimalPrepared?.width || 0"
                  :image-height="minimalPrepared?.height || 0"
                  :grayscale="grayscale"
                  @ready="applyMinimalCropPayload"
                  @complete="applyMinimalCropPayload"
                />
                <img
                  v-else-if="minimalPreviewUrl"
                  :src="minimalPreviewUrl"
                  alt="Ausgewähltes Bild"
                  class="minimal-canvas-image"
                >
                <div v-else class="minimal-canvas-empty">
                  <div class="minimal-canvas-icon">+</div>
                  <div class="minimal-canvas-copy">Bild hier ablegen oder auswählen</div>
                  <div class="minimal-canvas-sub">Das Bild wird direkt hier sichtbar. Falls nötig, setzen Sie den Ausschnitt sofort auf der festen Fläche.</div>
                </div>
              </div>
            </div>
            <label v-if="!minimalUploadDone" class="pick" :class="{ 'pick--minimal': isMinimalFlow }">
              <input type="file" :multiple="!isMinimalFlow" :accept="ACCEPT_IMAGE_INPUT" @change="onPick" />
              {{ isMinimalFlow ? 'Bild auswählen' : 'Dateien auswählen' }}
            </label>

            <div v-if="!minimalUploadDone" class="dz-hint muted" :class="{ 'dz-hint--minimal': isMinimalFlow }">
              {{ isMinimalFlow ? 'oder hier ein Bild ablegen (JPG/PNG/WEBP/TIFF)' : 'oder hier ablegen (JPG/PNG/WEBP/TIFF)' }}
            </div>
          </div>
        </div>

        <div v-if="!isMinimalFlow" class="row">
          <div class="row-head">
            <div class="row-title">{{ isSimpleEditorFlow ? '5. Anzahl und Schwarzweiß' : (isSimpleFlow ? '3. Anzahl und Schwarzweiß' : 'Einstellungen') }}</div>
            <div class="row-sub muted">{{ isSimpleEditorFlow ? 'Wird direkt für dieses Bild in den Warenkorb übernommen' : 'Wird auf alle hochgeladenen Bilder angewendet' }}</div>
          </div>

          <div class="row-body row-body--stack">
            <div class="settings-line">
              <div class="settings-label">Anzahl pro Bild</div>
              <div class="qty">
                <button class="qty-btn" type="button" :disabled="busy" @click="quantity = Math.max(1, Number(quantity || 1) - 1)">−</button>
                <input v-model.number="quantity" class="qty-input" type="number" min="1" step="1" inputmode="numeric" />
                <button class="qty-btn" type="button" :disabled="busy" @click="quantity = Math.max(1, Number(quantity || 1) + 1)">+</button>
              </div>
            </div>
            <div class="settings-line">
              <div class="settings-label">Schwarzweiß</div>
              <label class="switch">
                <input v-model="grayscale" type="checkbox" />
                <span class="switch-ui" :class="{ on: grayscale }" aria-hidden="true"><span /></span>
              </label>
            </div>
          </div>
        </div>

        <!-- Action -->
        <div class="actions" :class="{ 'actions--minimal': isMinimalFlow }">
          <button v-if="!minimalUploadDone" class="btn primary" :class="{ 'btn--minimal': isMinimalFlow }" :disabled="!canUpload" @click="uploadAndGo">
            {{ busy ? 'Uploading…' : (isMinimalFlow ? (cropRequired ? 'Upload & Ausschnitt setzen' : 'Upload & übernehmen') : (isSimpleEditorFlow ? 'Direkt in den Warenkorb' : (isSimpleFlow ? 'Weiter zum Ausschnitt' : 'Upload & weiter'))) }}
          </button>
          <button
            v-if="isMinimalFlow && !minimalUploadDone && minimalRecoveryState?.customerCanApproveOriginal"
            type="button"
            class="btn secondary btn--minimal-secondary"
            :disabled="minimalApproveLoading"
            @click="approveExistingRecoveryImage"
          >
            {{ minimalApproveLoading ? 'Speichere Freigabe…' : 'Vorhandenes Bild druckfreigeben' }}
          </button>

            <div class="hint">
            <span v-if="!categoryId || !productId">categoryId/productId fehlen in der URL.</span>
            <span v-else-if="minimalUploadDone">Nachlieferung abgeschlossen.</span>
            <span v-else-if="isSimpleEditorFlow && !effectiveProductId">Bitte zuerst ein Produkt auswählen.</span>
            <span v-else-if="!files.length">Wählen Sie zuerst Dateien aus.</span>
            <span v-else-if="!isPhoneBridgeMode && !selectedSizeKey">Wählen Sie zuerst eine Größe aus.</span>
            <span v-else-if="isSimpleEditorFlow">Das Bild wird nach dem Upload direkt in den Warenkorb gelegt.</span>
            <span v-else-if="isMinimalFlow && cropRequired">Ausschnitt jetzt direkt setzen und dann mit einem Klick hochladen.</span>
            <span v-else-if="isMinimalFlow">Bild wird direkt gespeichert, zusätzlicher Ausschnitt ist hier nicht nötig.</span>
            <span v-else-if="isPhoneBridgeMode">Upload landet direkt live im offenen Projekt.</span>
            <span v-else>&nbsp;</span>
          </div>
        </div>

        <div v-if="isSimpleEditorFlow && simpleEditorDone" class="simple-editor-success">
          <div class="simple-editor-success__icon">✓</div>
          <div class="simple-editor-success__copy">
            <div class="simple-editor-success__title">Im Warenkorb</div>
            <div class="simple-editor-success__text">{{ simpleEditorDoneNotice || 'Bild wurde direkt in den Warenkorb gelegt.' }}</div>
            <div class="simple-editor-success__meta">
              <span v-if="simpleEditorMeta.productTitle">{{ simpleEditorMeta.productTitle }}</span>
              <span v-if="simpleEditorMeta.sizeTitle">• {{ simpleEditorMeta.sizeTitle }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>


<style scoped>
.qu{
  --line: rgba(15,23,42,.12);
  --muted: rgba(15,23,42,.62);
  --muted2: rgba(15,23,42,.42);
  --bg: #fff;
  --soft: rgba(15,23,42,.03);
  --accent: rgba(239,68,68,.95);

  background: var(--bg);
  color: #0b1220;
  padding: 16px;
  display:flex;
  justify-content:center;
}

.qu--minimal{
  padding: 0;
  background: transparent;
}

.box{
  width: min(720px, 100%);
  border: 1px solid var(--line);
  background: #fff;
  padding: 14px;
  display:flex;
  flex-direction:column;
  gap: 12px;
}

.box--minimal{
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  gap: 10px;
}

.head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap: 12px;
  border-bottom: 1px solid var(--line);
  padding-bottom: 10px;
}

.kicker{
  font-size: 12px;
  color: var(--muted);
  text-transform: uppercase;
}
.title{
  margin-top: 4px;
  font-size: 18px;
  font-weight: 700;
}
.sub{
  margin-top: 2px;
  font-size: 13px;
  color: var(--muted);
}

.meta{
  display:flex;
  gap: 6px;
  flex-wrap:wrap;
  justify-content:flex-end;
}
.pill{
  border: 1px solid var(--line);
  padding: 6px 8px;
  font-size: 12px;
  color: rgba(15,23,42,.78);
  background:#fff;
}
.pill b{ font-weight: 700; color: rgba(15,23,42,.92); }

.content{
  display:flex;
  flex-direction:column;
  gap: 10px;
}

.simple-editor-panel{
  display:grid;
  gap: 10px;
  border: 1px solid rgba(15,23,42,.10);
  background: linear-gradient(180deg, rgba(248,250,252,.92), #fff 42%);
  border-radius: 18px;
  padding: 14px;
}

.simple-editor-panel__head{
  display:flex;
  flex-direction:column;
  gap: 4px;
}

.simple-editor-panel__kicker{
  font-size: 11px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: rgba(15,23,42,.46);
}

.simple-editor-panel__title{
  font-size: 18px;
  line-height: 1.2;
  font-weight: 800;
  color: #0f172a;
}

.minimal-meta-card{
  display:flex;
  flex-direction:column;
  gap: 12px;
  border: 1px solid rgba(15,23,42,.12);
  background: #fff;
  border-radius: 18px;
  padding: 14px;
}

.minimal-meta-top{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap: 12px;
}

.minimal-meta-copy{
  min-width: 0;
  display:flex;
  flex-direction:column;
  gap: 4px;
}

.minimal-product-line{
  font-size: 18px;
  line-height: 1.3;
  font-weight: 800;
  color: #0f172a;
}

.minimal-label{
  font-size: 13px;
  line-height: 1.5;
  color: rgba(15,23,42,.62);
}

.minimal-meta-grid{
  display:grid;
  gap: 6px 14px;
  font-size: 13px;
  line-height: 1.55;
  color: rgba(15,23,42,.74);
}

.minimal-option-list{
  display:flex;
  flex-wrap:wrap;
  gap: 8px;
}

.minimal-pill{
  display:inline-flex;
  align-items:center;
  min-height: 30px;
  border-radius: 999px;
  border: 1px solid rgba(15,23,42,.12);
  background: #fff;
  color: #334155;
  padding: 0 11px;
  font-size: 12px;
  font-weight: 700;
}

.minimal-pill--success{
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #166534;
}

.minimal-pill--warning{
  border-color: #fdba74;
  background: #fff7ed;
  color: #9a3412;
}

.steps{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}

.step-pill{
  border: 1px solid var(--line);
  background: var(--soft);
  padding: 6px 10px;
  font-size: 12px;
  color: rgba(15,23,42,.78);
}

.row{
  border: 1px solid var(--line);
  background: #fff;
  padding: 10px;
  display:flex;
  flex-direction:column;
  gap: 8px;
}

.row--simple{
  border-radius: 14px;
  border-color: rgba(15,23,42,.10);
  background: #fff;
  box-shadow: 0 8px 24px rgba(15,23,42,.03);
}

.row-head{
  display:flex;
  align-items:baseline;
  justify-content:space-between;
  gap: 10px;
}
.row-title{ font-size: 13px; font-weight: 700; }
.row-sub{ font-size: 12px; }

.row-body{
  display:flex;
  align-items:center;
  gap: 10px;
  flex-wrap:wrap;
}

.row-body--minimal-upload{
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.row-body--stack{
  align-items:stretch;
  flex-direction:column;
}

.variant-line{
  display:flex;
  flex-direction:column;
  gap: 6px;
}

.variant-label{
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.simple-editor-success{
  display:flex;
  align-items:center;
  gap: 14px;
  border: 1px solid rgba(16,185,129,.18);
  background: #ecfdf5;
  border-radius: 18px;
  padding: 14px;
}

.simple-editor-success__icon{
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: #10b981;
  color: #fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size: 22px;
  font-weight: 800;
  flex: 0 0 auto;
}

.simple-editor-success__copy{
  display:grid;
  gap: 4px;
  min-width: 0;
}

.simple-editor-success__title{
  font-size: 16px;
  line-height: 1.2;
  font-weight: 800;
  color: #065f46;
}

.simple-editor-success__text{
  font-size: 13px;
  line-height: 1.55;
  color: rgba(6,95,70,.86);
}

.simple-editor-success__meta{
  font-size: 12px;
  line-height: 1.4;
  color: rgba(6,95,70,.72);
  font-weight: 700;
}

.minimal-stage{
  width: 100%;
  display: flex;
  justify-content: center;
  position: relative;
}

.minimal-canvas{
  width: min(100%, 270px);
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  border: 1px solid rgba(15,23,42,.12);
  background: #f8fafc;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.minimal-canvas--filled{
  background: #f8fafc;
}

.minimal-cropper{
  width: calc(100% - 18px);
  height: calc(100% - 18px);
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.minimal-cropper :deep(.cc){
  border-radius: 10px;
}

.minimal-cropper :deep(.cc-canvas){
  border-radius: 10px;
}

.minimal-canvas-image{
  width: calc(100% - 18px);
  height: calc(100% - 18px);
  object-fit: contain;
  border-radius: 10px;
  display: block;
}

.minimal-canvas-empty{
  width: calc(100% - 18px);
  height: calc(100% - 18px);
  border-radius: 10px;
  border: 1px dashed rgba(15,23,42,.18);
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  gap: 10px;
}

.minimal-canvas-icon{
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: rgba(15,23,42,.06);
  color: rgba(15,23,42,.55);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  line-height: 1;
}

.minimal-canvas-copy{
  font-size: 14px;
  font-weight: 700;
  color: #0b1220;
}

.minimal-canvas-sub{
  font-size: 12px;
  line-height: 1.55;
  color: rgba(15,23,42,.58);
  max-width: 190px;
}

.minimal-success{
  border-radius: 16px;
  background: rgba(255,255,255,.98);
  border: 1px solid rgba(34,197,94,.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 22px;
  gap: 12px;
}

.minimal-success--standalone{
  position: static;
  inset: auto;
  width: min(100%, 320px);
}

.minimal-success-preview{
  width: min(100%, 280px);
  border-radius: 10px;
  border: 1px solid rgba(22,163,74,.14);
  background: #fff;
  overflow: hidden;
}

.minimal-success-preview-frame{
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: #fff;
}

.minimal-success-preview-image{
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
}

.minimal-success-preview-image--grayscale{
  filter: grayscale(1);
}

.minimal-success-meta{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap: 10px;
}

.minimal-success-banner{
  display:inline-flex;
  align-items:center;
  gap: 8px;
  min-height: 34px;
  border-radius: 999px;
  border: 1px solid rgba(22,163,74,.16);
  background: #f0fdf4;
  color: #166534;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
}

.minimal-success-banner-dot{
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #16a34a;
  flex: 0 0 auto;
}

.minimal-success-icon{
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: #16a34a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 800;
  box-shadow: 0 10px 24px rgba(22,163,74,.28);
}

.minimal-success-title{
  font-size: 18px;
  line-height: 1.2;
  font-weight: 800;
  color: #14532d;
}

.minimal-success-copy{
  max-width: 210px;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(20,83,45,.82);
}

.minimal-success-btn{
  height: 34px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgba(21,128,61,.18);
  background: #fff;
  color: #166534;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.settings-line{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 12px;
  flex-wrap:wrap;
}

.settings-label{
  font-size: 14px;
  font-weight: 700;
  color: #0b1220;
}

.qty{
  display:flex;
  align-items:center;
  gap:8px;
}

.qty-btn{
  width:34px;
  height:34px;
  border:1px solid var(--line);
  background:#fff;
  cursor:pointer;
  font-size:18px;
}

.qty-input{
  width:72px;
  height:34px;
  border:1px solid var(--line);
  text-align:center;
  font-size:14px;
}

.switch{
  display:inline-flex;
  align-items:center;
  position:relative;
}

.switch input{
  position:absolute;
  opacity:0;
  pointer-events:none;
}

.switch-ui{
  width:48px;
  height:28px;
  border-radius:999px;
  background:#dbe3ec;
  position:relative;
  transition:background .18s ease;
}

.switch-ui > span{
  position:absolute;
  top:3px;
  left:3px;
  width:22px;
  height:22px;
  border-radius:999px;
  background:#fff;
  transition:transform .18s ease;
  box-shadow:0 1px 3px rgba(15,23,42,.18);
}

.switch-ui.on{
  background:#b30610;
}

.switch-ui.on > span{
  transform:translateX(20px);
}

.dz{
  background: #fafafa;
  border: 2px dashed rgba(15,23,42,.18);
}

.dz--minimal{
  border-radius: 12px;
  padding: 14px;
  min-height: 220px;
  justify-content: center;
  background: #fafafa;
  border: 1px dashed rgba(15,23,42,.16);
  box-shadow: none;
}

.dz.over{
  border-color: rgba(239,68,68,.75);
  background: rgba(239,68,68,.06);
}

.dz--minimal.over{
  border-color: rgba(179,6,16,.75);
  background: #fff5f5;
  box-shadow: none;
}

.pick{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--line);
  background: #fff;
  cursor: pointer;
  font-weight: 700;
}
.pick input{ display:none; }

.pick--minimal{
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  border-color: rgba(179,6,16,.18);
  background: #fff;
  box-shadow: none;
  color: #b30610;
  font-size: 13px;
}

.dz-hint{ font-size: 12px; }

.dz-hint--minimal{
  font-size: 13px;
  text-align: center;
  max-width: 320px;
  line-height: 1.55;
}

.minimal-note,
.minimal-success-note{
  width: 100%;
  max-width: 100%;
  border: 1px solid rgba(15,23,42,.12);
  background: #fff;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.55;
  color: rgba(15,23,42,.74);
}

.minimal-success-note{
  margin-top: 4px;
}

.actions{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 12px;
  flex-wrap:wrap;
  border-top: 1px solid var(--line);
  padding-top: 10px;
}

.actions--minimal{
  border-top: 0;
  padding-top: 2px;
  justify-content: stretch;
}

.btn{
  height: 40px;
  border: 1px solid var(--line);
  background: #fff;
  cursor:pointer;
  font-weight: 700;
  padding: 0 14px;
}
.btn:disabled{ opacity: .5; cursor:not-allowed; }
.btn.primary{
  border-color: rgba(239,68,68,.35);
  box-shadow: 0 0 0 4px rgba(239,68,68,.08);
}

.btn--minimal{
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border-color: #b30610;
  background: #b30610;
  color: #fff;
  box-shadow: none;
}

.btn--minimal-secondary{
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border-color: rgba(15,23,42,.14);
  background: #fff;
  color: #0f172a;
  box-shadow: none;
}

.hint{
  min-height: 18px;
  font-size: 12px;
  color: var(--muted2);
}

.state{ padding: 8px 0; color: var(--muted); }
.err{ padding: 8px 0; color: #b91c1c; font-weight: 700; }

.muted{ color: var(--muted); }

@media (max-width: 640px){
  .head{ flex-direction: column; }
  .meta{ justify-content:flex-start; }
  .actions{ justify-content:flex-start; }
}
</style>
